import React, { useContext, useRef, useState } from "react";
import { AuthContext, IntlContext } from "context";
import blobToBase64 from "helpers/file/blobToBase64";
import { ACCEPTED_DOCUMENT_MIMES, UPLOAD_MAX_MEGABYTES, isFileTooLarge } from "helpers/file/uploadConstraints";
import { useParams } from "react-router";
import { createMessage } from "hooks/message";
import type { ChatMessage, ChatSocket } from "../types";
import { Send, Smile, Paperclip } from "lucide-react";
import { toast } from "sonner";
interface Props {
    socket: ChatSocket;
    messagesReceived: ChatMessage[];
    setMessagesReceived: (messagesReceived: ChatMessage[]) => void;
    refreshDataConversation: () => void;
    refreshDataActiveConversation: () => void;
    interlocutorUserId: string | undefined;
}

const COMMON_EMOJIS = [
    "😊",
    "😂",
    "❤️",
    "👍",
    "🙏",
    "😍",
    "🎉",
    "🔥",
    "😢",
    "😮",
    "🤔",
    "👋",
    "✅",
    "⭐",
    "💪",
    "🙂",
    "😅",
    "🤝",
    "👌",
    "💬",
    "📅",
    "⏰",
    "✨",
    "🏠",
];

const InputSendMessage: React.FC<Props> = ({
    socket,
    messagesReceived,
    setMessagesReceived,
    refreshDataConversation,
    refreshDataActiveConversation,
    interlocutorUserId,
}) => {
    const [message, setMessage] = useState<string>("");
    const [showEmojiPicker, setShowEmojiPicker] = useState<boolean>(false);
    const { service_id: serviceId } = useParams() as { service_id: string };
    const { client_id: clientId } = useParams() as { client_id: string };
    const concatServiceClientId = serviceId + clientId;
    const auth = useContext(AuthContext);
    const intl = useContext(IntlContext);
    const refInputImage = useRef<HTMLInputElement | null>(null);
    const textareaRef = useRef<HTMLTextAreaElement | null>(null);
    const emojiPickerRef = useRef<HTMLDivElement | null>(null);

    const onClickToUploadFile = () => {
        refInputImage.current?.click();
    };

    const insertEmoji = (emoji: string) => {
        const textarea = textareaRef.current;
        if (!textarea) {
            setMessage((prev) => prev + emoji);
            return;
        }
        const start = textarea.selectionStart ?? message.length;
        const end = textarea.selectionEnd ?? message.length;
        const newValue = message.slice(0, start) + emoji + message.slice(end);
        setMessage(newValue);
        // Restore cursor after emoji
        requestAnimationFrame(() => {
            textarea.focus();
            const pos = start + emoji.length;
            textarea.setSelectionRange(pos, pos);
        });
        setShowEmojiPicker(false);
    };

    const sendFile = async ({ target }: React.ChangeEvent<HTMLInputElement>) => {
        const currentUserId = auth.account?.user.id;
        if (!currentUserId) return;

        let blobFile: { name: string; size: number; type: string; content: File | string } | null = null;
        if (target.files) {
            const file = target.files.item(0);
            if (file) {
                if (isFileTooLarge(file)) {
                    toast.error(intl.formatMessage("generic.upload.too_large", { max: UPLOAD_MAX_MEGABYTES }));
                    target.value = "";
                    return;
                }
                blobFile = Object.assign({ name: file.name, size: file.size, type: file.type }, { content: file });
            }
        }

        // Allow re-selecting the same file after an error or a send.
        target.value = "";

        if (blobFile?.content && blobFile.content instanceof Blob) {
            blobFile.content = await blobToBase64(blobFile.content);
        }

        const data = {
            service_id: serviceId,
            client_id: clientId,
            user_id: currentUserId,
            file: blobFile,
        };

        try {
            const dataFile = await createMessage(data);
            socket.emit("send_message", { room: concatServiceClientId });
            if (interlocutorUserId) {
                socket.emit("refresh_threads_with_new_message", {
                    user_id: interlocutorUserId,
                });
            }
            setMessagesReceived([
                ...messagesReceived,
                {
                    user_id: dataFile.user_id,
                    client_id: dataFile.client_id,
                    service_id: dataFile.service_id,
                    message: null,
                    file_id: dataFile.id,
                    file: dataFile.file,
                    created_at: dataFile.created_at,
                },
            ]);
            setMessage("");
        } catch {
            toast.error(intl.formatMessage("generic.upload.failed"));
            refreshDataActiveConversation();
        }
    };

    const sendMessage = async () => {
        if (!message.trim()) return;
        const currentUserId = auth.account?.user.id;
        if (!currentUserId) return;

        try {
            await createMessage({
                service_id: serviceId,
                client_id: clientId,
                user_id: currentUserId,
                message,
            });
            socket.emit("send_message", { room: concatServiceClientId });
            if (interlocutorUserId) {
                socket.emit("refresh_threads_with_new_message", {
                    user_id: interlocutorUserId,
                });
            }
            setMessagesReceived([
                ...messagesReceived,
                {
                    service_id: serviceId,
                    client_id: clientId,
                    user_id: currentUserId,
                    message,
                    created_at: new Date().toISOString(),
                },
            ]);
            setMessage("");
            refreshDataConversation();
        } catch {
            refreshDataActiveConversation();
        }
    };

    if (!serviceId || !clientId) return null;

    return (
        <div className="px-4 py-3 bg-white border-t border-border">
            <input
                type="file"
                ref={refInputImage}
                className="hidden"
                onChange={sendFile}
                accept={ACCEPTED_DOCUMENT_MIMES}
            />
            <div className="flex items-center gap-2">
                <button
                    type="button"
                    onClick={onClickToUploadFile}
                    className="flex-shrink-0 w-9 h-9 rounded-full flex items-center justify-center text-muted-foreground hover:bg-secondary transition-colors"
                    title={intl.formatMessage("booking.messages.label.attach_file")}
                >
                    <Paperclip className="w-4 h-4" />
                </button>

                {/* Emoji picker */}
                <div className="relative flex-shrink-0" ref={emojiPickerRef}>
                    <button
                        type="button"
                        onClick={() => setShowEmojiPicker((v) => !v)}
                        className="w-9 h-9 rounded-full flex items-center justify-center text-muted-foreground hover:bg-secondary transition-colors"
                        title={intl.formatMessage("booking.messages.label.add_emoji")}
                    >
                        <Smile className="w-4 h-4" />
                    </button>
                    {showEmojiPicker && (
                        <div className="absolute bottom-full left-0 mb-2 z-50 bg-white border border-border rounded-2xl shadow-lg p-2 w-56">
                            <div className="grid grid-cols-8 gap-1">
                                {COMMON_EMOJIS.map((emoji) => (
                                    <button
                                        key={emoji}
                                        type="button"
                                        onClick={() => insertEmoji(emoji)}
                                        className="text-xl hover:bg-secondary rounded-lg p-0.5 transition-colors leading-none"
                                        title={emoji}
                                    >
                                        {emoji}
                                    </button>
                                ))}
                            </div>
                        </div>
                    )}
                </div>

                <div className="flex-1 relative">
                    <textarea
                        ref={textareaRef}
                        name="send_message"
                        placeholder={intl.formatMessage("booking.messages.label.send_message")}
                        value={message}
                        rows={1}
                        className="w-full resize-none rounded-2xl border border-input bg-secondary/50 px-4 py-2.5 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition-colors max-h-32 overflow-y-auto"
                        onChange={(e) => setMessage(e.target.value)}
                        onKeyDown={(e) => {
                            if (e.key === "Enter" && (e.ctrlKey || e.metaKey)) {
                                e.preventDefault();
                                void sendMessage();
                            }
                        }}
                        onFocus={() => setShowEmojiPicker(false)}
                    />
                </div>

                <button
                    type="button"
                    onClick={() => void sendMessage()}
                    disabled={!message.trim()}
                    className="flex-shrink-0 w-9 h-9 rounded-full flex items-center justify-center bg-primary text-primary-foreground hover:bg-primary/90 disabled:opacity-40 disabled:cursor-not-allowed transition-all"
                    title={intl.formatMessage("generic.action.send")}
                >
                    <Send className="w-4 h-4" />
                </button>
            </div>
            <p className="text-xs text-muted-foreground mt-1.5 text-center">
                {intl.formatMessage("booking.messages.label.send_hint")}
            </p>
        </div>
    );
};

export default InputSendMessage;
